import { forwardRef } from 'react';
import { Box, BoxProps } from './box';

/**
 * FlexRow component - A convenience wrapper for horizontal flex layouts.
 *
 * This is the most common Box pattern (45%+ of mobile app usage).
 * Pre-configured with `display="flex"`, `alignItems="center"`, and `gap={1}`.
 *
 * Based on analysis of 43+ files in mobile app showing this as the dominant pattern.
 *
 * @example
 * // Basic action group
 * <FlexRow>
 *   <Button>Action 1</Button>
 *   <Button>Action 2</Button>
 * </FlexRow>
 *
 * @example
 * // Custom gap
 * <FlexRow gap={2}>
 *   <Icon />
 *   <Typography>Label</Typography>
 * </FlexRow>
 *
 * @example
 * // Override defaults
 * <FlexRow alignItems="flex-start" gap={0.5}>
 *   <Avatar />
 *   <Box>Content</Box>
 * </FlexRow>
 */
export interface FlexRowProps extends BoxProps {
  /**
   * Gap between flex items (theme spacing units).
   * @default 1 (8px)
   */
  gap?: BoxProps['gap'];
  /**
   * Alignment of items on the cross axis.
   * @default 'center'
   */
  alignItems?: BoxProps['alignItems'];
}

export const FlexRow = forwardRef<HTMLDivElement, FlexRowProps>(
  ({ gap = 1, alignItems = 'center', ...props }, ref) => (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <Box ref={ref as any} display="flex" alignItems={alignItems} gap={gap} {...props} />
  )
);

FlexRow.displayName = 'FlexRow';
